import { useTour } from '@reactour/tour'
import {
  ArrowAllCalendars,
  ContactUs,
  GuidedTourIcon,
  MenuUserGuideIcon
} from 'assets/icons'
import { MenuWhatsNew } from 'assets/icons/menu'
import {
  ONBOARDING_MODE,
  useTfmTourProvider
} from 'components/onboarding/TFMTourProvider'
import PopupSection from 'components/popup/PopupSection'
import PopupSectionRow from 'components/popup/PopupSectionRow'
import i18n from 'i18n'
import { t } from 'i18next'
import { useEffect, useState } from 'react'
import {
  fetchWhatsNewPosts,
  markWhatsNewAsSeen,
  prefetchLatestWhatsNewPost,
  WhatsNewPost
} from 'services/api/whatsNew'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { is24meApp, isGroupcalApp } from 'utils/appType'

import ContactUsPopup from './ContactUsPopup'
import WhatsNewDialog from './WhatsNewDialog'

export interface HelpCenterSectionProps {
  setPopupOpen: (b: boolean) => void
}

export default function HelpCenterSection(props: HelpCenterSectionProps) {
  const [showContactUs, setShowContactUs] = useState(false)
  const [showWhatsNewDialog, setShowWhatsNewDialog] = useState(false)

  const [latestPostId, setLatestPostId] = useState<number | null>(null)
  const [whatsNewPosts, setWhatsNewPosts] = useState<WhatsNewPost[]>([])
  const [isWhatsNewLoading, setIsWhatsNewLoading] = useState(false)
  const [whatsNewError, setWhatsNewError] = useState<string | null>(null)

  const onContactUs = () => {
    setShowContactUs(true)
  }

  const { setIsOpen, setCurrentStep, setSteps } = useTour()

  const tfmTour = useTfmTourProvider()

  const markLatestAsSeen = (candidateLatestId?: number | null) => {
    const latestId = candidateLatestId ?? latestPostId ?? whatsNewPosts[0]?.id

    if (latestId) {
      markWhatsNewAsSeen(latestId)
      setLatestPostId(latestId)
    }
  }

  const onOpenWhatsNewHistory = async () => {
    if (isWhatsNewLoading) {
      return
    }

    setShowWhatsNewDialog(true)
    setIsWhatsNewLoading(true)
    setWhatsNewError(null)
    setWhatsNewPosts([])
    markLatestAsSeen()

    try {
      const posts = await fetchWhatsNewPosts(10, 1)
      setWhatsNewPosts(posts)

      const latestFetchedId = posts[0]?.id
      if (latestFetchedId) {
        setLatestPostId(latestFetchedId)
        markLatestAsSeen(latestFetchedId)
      }
    } catch {
      setWhatsNewError(t('failedToLoad', { defaultValue: 'Failed to load' }))
      setWhatsNewPosts([])
    } finally {
      setIsWhatsNewLoading(false)
    }
  }

  const onDismissWhatsNew = () => {
    markLatestAsSeen()
    setShowWhatsNewDialog(false)
  }

  const onGuidedTour = () => {
    LocalStorage.set(LocalStorageKey.USER_FINSHED_ONBOARDING, '0')
    LocalStorage.remove(LocalStorageKey.STATE_DICT)

    tfmTour.setMode(ONBOARDING_MODE.FIRST_LOGIN)
    tfmTour.reset()

    if (setSteps && tfmTour.getStepsByMode) {
      setSteps(tfmTour.getStepsByMode(ONBOARDING_MODE.FIRST_LOGIN))
    }
    setCurrentStep(0)
    setIsOpen(true)

    props.setPopupOpen(false)
  }

  return (
    <PopupSection title={`${t('helpCenter')}`} popupSectionGap={1}>
      {isGroupcalApp() && (
        <PopupSectionRow
          actionId="guided-tour-button"
          icon={<GuidedTourIcon />}
          title={t('guidedTour')}
          iconClass="icon-regular"
          maxWidthIcon={20}
          onlyTitle
          actionBg
          engulfEntireRow
          action={onGuidedTour}
        />
      )}
      <ContactUsPopup
        open={showContactUs}
        setOpen={function (b: boolean): void {
          setShowContactUs(b)
        }}
        setMenuOpen={props.setPopupOpen}
      >
        <span>
          <PopupSectionRow
            icon={<ContactUs />}
            title={t('contactUs')}
            iconClass="icon-regular"
            maxWidthIcon={20}
            action={onContactUs}
            actionBg
            engulfEntireRow
            value={
              <ArrowAllCalendars
                style={{
                  transform: i18n.dir() === 'ltr' ? '' : 'rotate(180deg)',
                  cursor: 'pointer',
                  marginTop: 4
                }}
              />
            }
          />
        </span>
      </ContactUsPopup>
      {/* We hide entry for 24me app purposely */}
      {isGroupcalApp() && (
        <PopupSectionRow
          actionId="whats-new-button"
          title={t('whatsNew', { defaultValue: "What's new" })}
          icon={<MenuWhatsNew />}
          iconClass="icon-regular"
          maxWidthIcon={20}
          actionBg
          engulfEntireRow
          action={onOpenWhatsNewHistory}
        />
      )}
      <PopupSectionRow
        title={t('userGuide')}
        icon={<MenuUserGuideIcon />}
        iconClass="icon-regular"
        maxWidthIcon={20}
        actionBg
        engulfEntireRow
        action={() => {
          window.open(
            is24meApp()
              ? 'https://24me.zendesk.com/'
              : 'https://www.groupcal.app/guide/',
            '_blank'
          )
        }}
      />
      <WhatsNewDialog
        open={showWhatsNewDialog}
        setOpen={setShowWhatsNewDialog}
        posts={whatsNewPosts}
        loading={isWhatsNewLoading}
        error={whatsNewError}
        onClose={onDismissWhatsNew}
      />
    </PopupSection>
  )
}
